import { useState } from 'react'

import { ActionIcon, Box, Collapse, Loader, Tooltip } from '@mantine/core'
import { Info } from 'lucide-react'

import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import IconChevronDown from 'src/icons/IconChevronDown'
import { cn } from 'src/utils'

const AnalyticsCard = ({
  title,
  description,
  info = null,
  badgeText = 'No data',
  loading = false,
  showBadge = false,
  headerChildren = null,
  children,
}: {
  title: string
  description?: string
  info?: string
  badgeText?: string
  loading?: boolean
  showBadge?: boolean
  headerChildren?: React.ReactNode
  children: React.ReactNode
}) => {
  const [opened, setOpened] = useState(true)

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <Box
        className="flex w-full cursor-pointer flex-row items-center gap-2"
        onClick={() => setOpened((prev) => !prev)}
      >
        <ActionIcon variant="subtle" size="sm">
          <IconChevronDown
            className={cn(
              'h-4 w-4 fill-none stroke-gray-500 transition-all',
              !opened && '-rotate-90'
            )}
          />
        </ActionIcon>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {info && (
          <Tooltip
            label={
              <span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                {info}
              </span>
            }
            multiline
            w={320}
            withArrow
            position="top"
          >
            <Info
              size={14}
              className="cursor-help text-gray-400"
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        )}
        {showBadge && (
          <StyledBadge variant="light" className="rounded" color="blue">
            {badgeText}
          </StyledBadge>
        )}
        <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
          {headerChildren}
        </div>
      </Box>

      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-1 py-3">
          {loading ? (
            <Loader
              className="flex flex-1 items-center justify-center"
              size={'xs'}
              color="gray"
            />
          ) : (
            <div className="flex flex-1 flex-col gap-4 overflow-auto">
              {children}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}

export default AnalyticsCard
